import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { postRecordingSessionAudio } from '@/features/yakureki/apis/recordingSession'
import { AudioSource } from '@/features/yakureki/apis/audio'
import { executeSuperReload } from '@/shared/helpers/superReload'
import { useToast } from '@/shared/contexts/ToastContext'

interface PostRecordingSessionVariables {
  audioSource: AudioSource
  yakurekiIds: string[]
  pharmacistId?: string
  memoItems?: string[]
}

/**
 * グループセッション音声アップロードMutation
 *
 * 単一録音 (useAudioMutations.ts) と同じく以下を実行:
 * - executeSuperReload で localStorage の差分同期キャッシュを一掃 + 全件再取得.
 *   これを省くと usePatientListQuery が古い localStorage 起点で delta merge を行い、
 *   pending 行などが再取得されず一覧上で件数が減って見える.
 * - patients.all を invalidate (一覧の status 反映)
 * - 対象 event 全件分の yakureki.detail(id) を invalidate
 *   (PostRecordingScreen の screenMode 遷移即時化)
 */
export const usePostRecordingSessionMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      audioSource,
      yakurekiIds,
      pharmacistId,
      memoItems,
    }: PostRecordingSessionVariables) => {
      const recordingSession = await postRecordingSessionAudio(
        audioSource,
        yakurekiIds,
        pharmacistId,
        memoItems
      )
      await executeSuperReload()
      await queryClient.invalidateQueries({
        queryKey: queryKeys.patients.all,
      })
      recordingSession?.yakurekis?.forEach((y) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.yakureki.detail(y.id),
        })
      })
      return recordingSession
    },
    onError: (error) => {
      const axiosError = error as {
        response?: { status?: number; data?: { error?: string } }
      }
      const status = axiosError.response?.status
      const errorMessage = axiosError.response?.data?.error

      if (status === 400) {
        showToast(
          errorMessage || 'リクエストが無効です（2名以上選択してください）',
          'error',
          5000
        )
      } else if (status === 404) {
        showToast('対象の患者が見つかりません', 'error', 5000)
      } else if (!navigator.onLine) {
        showToast('ネットワーク接続を確認してください', 'error', 5000)
      } else {
        showToast('まとめて録音のアップロードに失敗しました', 'error', 5000)
      }
    },
  })
}
